import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'underscore';

import { checkAuth } from '../../../actions';
import AdminShell from '../layout/AdminShell';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SearchInput from '../ui/SearchInput';
import EmptyState from '../ui/EmptyState';
import LazyListFooter from '../ui/LazyListFooter';
import ApplicationRow from '../applications/ApplicationRow';
import ApplicationStats from '../applications/ApplicationStats';
import NewApplicationModal from '../applications/NewApplicationModal';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useLazyList from '../hooks/useLazyList';
import { PlusIcon, RefreshIcon, UsersIcon, SortIcon } from '../ui/icons';

// Redesign of newadmin/Applications.js. Search/sort logic is a straight port
// of onInputChange/sort/getMostRecentDate, same behavior, computed with
// useMemo instead of state copies.
//
// Search is kept off the keystroke path in three steps, because on a big
// account every keypress used to rebuild every search string, re-sort the
// whole list and re-render every row (a multi-second main-thread stall):
//   1. the searchable string per application is built once per fetch (index)
//   2. sorting only re-runs when the sort itself changes, not per keystroke
//   3. the term that drives filtering is debounced, and only a chunk of the
//      results is mounted at a time (useLazyList)
const ROWS_PER_CHUNK = 25;
const getMostRecentDate = (app) => {
    const lastUpdated = new Date(app.last_updated || 0);
    const created = new Date(app.created || 0);
    return Math.max(lastUpdated.getTime(), created.getTime());
};

const getName = (form) => {
    let name = '';
    if (form.completed === true) {
        name = `${form.data.General_Details.applicant1.given_name} ${form.data.General_Details.applicant1.family_name}`;
    }
    if (
        form.completed &&
        form.data.General_Details.applicant2 != undefined &&
        'given_name' in form.data.General_Details.applicant2 &&
        form.data.General_Details.applicant2.given_name != '' &&
        'family_name' in form.data.General_Details.applicant2 &&
        form.data.General_Details.applicant2.family_name != ''
    ) {
        name = name + ` & ${form.data.General_Details.applicant2.given_name} ${form.data.General_Details.applicant2.family_name}`;
    }
    return name;
};

const buildSearchString = (application) => {
    let searchString = `${application.name.toLowerCase()}`;
    Object.keys(application.applicants || {}).forEach((key) => {
        const applicant = application.applicants[key];
        let string = '';
        if (applicant.name != undefined) string = string + `${applicant.name.toLowerCase()}`;
        if (applicant.mobile != undefined) string = string + `${applicant.mobile.toLowerCase()}`;
        if (applicant.email != undefined) string = string + `${applicant.email.toLowerCase()}`;
        searchString = searchString + ` ${string}`;
    });
    return searchString;
};

const SortableHeader = ({ label, sortKey, activeKey, onSort, className = '' }) => (
    <th
        className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-[#6B7280] cursor-pointer select-none hover:text-[#1F2937] transition-colors ${className}`}
        onClick={() => onSort(sortKey)}
    >
        <span className="inline-flex items-center gap-1">
            {label}
            <SortIcon className={`h-3.5 w-3.5 ${activeKey === sortKey ? 'text-[#7C5CFC]' : 'text-[#D1D5DB]'}`} />
        </span>
    </th>
);

const ApplicationsPage = () => {
    const applications = useSelector((s) => s.admin.applications);
    const dispatch = useDispatch();

    const [searchTerm, setSearchTerm] = useState('');
    const [sortKey, setSortKey] = useState('recent_date');
    const [sortOrder, setSortOrder] = useState('-');
    const [addOpen, setAddOpen] = useState(false);

    // Same toggle behavior as the old sort(): every click flips the order.
    const handleSort = (key) => {
        setSortKey(key);
        setSortOrder((prev) => (prev === '+' ? '-' : '+'));
    };

    // Only the filtering waits for the debounce. The input itself stays fully
    // controlled, so typing never feels laggy.
    const debouncedTerm = useDebouncedValue(searchTerm, 200);

    // One pass per fetch instead of one pass per keystroke.
    const searchIndex = useMemo(
        () =>
            (applications || []).map((application) => ({
                application,
                haystack: buildSearchString(application),
            })),
        [applications]
    );

    // Sorted once per sort change. Filtering below preserves this order, so the
    // result is identical to the old filter-then-sort.
    const sortedIndex = useMemo(() => {
        let sorted;
        if (sortKey === 'recent_date' || sortKey === 'created') {
            sorted = _.sortBy(searchIndex, (entry) => -getMostRecentDate(entry.application));
        } else {
            sorted = _.sortBy(searchIndex, (entry) => entry.application[sortKey]);
        }
        if (sortOrder === '+') {
            sorted = [...sorted].reverse();
        }
        return sorted;
    }, [searchIndex, sortKey, sortOrder]);

    const visibleApplications = useMemo(() => {
        const term = debouncedTerm.toLowerCase();
        const matches = term === '' ? sortedIndex : sortedIndex.filter((entry) => entry.haystack.includes(term));
        return matches.map((entry) => entry.application);
    }, [sortedIndex, debouncedTerm]);

    const { visible, visibleCount, total, hasMore, loadMore, sentinelRef } = useLazyList(visibleApplications, {
        pageSize: ROWS_PER_CHUNK,
    });

    // Port of refreshApplications: checkAuth refreshes the token and
    // refetches applications + events.
    const refreshApplications = () => {
        dispatch(checkAuth());
    };

    const renderRows = (variant) =>
        visible.map((application) => (
            <ApplicationRow
                key={application.id}
                application={application}
                name={getName(application) || application.name}
                variant={variant}
            />
        ));

    return (
        <AdminShell title="Applications">
            <NewApplicationModal open={addOpen} onClose={() => setAddOpen(false)} />

            <ApplicationStats applications={applications} />

            <Card
                title="Applications"
                subtitle={`${total} application${total === 1 ? '' : 's'}`}
                actions={
                    <>
                        <SearchInput
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full sm:w-64"
                        />
                        <Button onClick={() => setAddOpen(true)}>
                            <PlusIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">New Application</span>
                            <span className="sm:hidden">New</span>
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={refreshApplications}
                            title="Refresh Applications"
                            aria-label="Refresh Applications"
                            className="!px-3"
                        >
                            <RefreshIcon className="h-4 w-4" />
                        </Button>
                    </>
                }
                bodyClassName="p-0"
            >
                {total === 0 ? (
                    <EmptyState
                        icon={<UsersIcon className="h-6 w-6" />}
                        title={searchTerm ? 'No applications match your search' : 'No applications yet'}
                        subtitle={
                            searchTerm
                                ? 'Try a different name, mobile or email.'
                                : 'Create your first application to get started.'
                        }
                        action={
                            searchTerm ? null : (
                                <Button onClick={() => setAddOpen(true)}>
                                    <PlusIcon className="h-4 w-4" />
                                    New Application
                                </Button>
                            )
                        }
                    />
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-[#F0F1F5]">
                                        <SortableHeader label="Name" sortKey="name" activeKey={sortKey} onSort={handleSort} className="pl-4 sm:pl-6" />
                                        <SortableHeader label="Last Update" sortKey="recent_date" activeKey={sortKey} onSort={handleSort} />
                                        <SortableHeader label="Date Created" sortKey="created" activeKey={sortKey} onSort={handleSort} className="pr-4 sm:pr-6" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#F0F1F5]">{renderRows('row')}</tbody>
                            </table>
                        </div>

                        {/* Mobile card list */}
                        <div className="md:hidden divide-y divide-[#F0F1F5]">{renderRows('card')}</div>

                        <LazyListFooter
                            sentinelRef={sentinelRef}
                            hasMore={hasMore}
                            onLoadMore={loadMore}
                            visibleCount={visibleCount}
                            total={total}
                            noun="applications"
                            className="border-t border-[#F0F1F5]"
                        />
                    </>
                )}
            </Card>
        </AdminShell>
    );
};

export default ApplicationsPage;
